'use client';

import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Link from 'next/link';

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Simple, transparent{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              pricing
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Choose the plan that fits your needs. All plans include our core AI features.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Free Plan */}
            <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl border border-gray-700">
              <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$0</span>
                <span className="text-gray-400">/month</span>
              </div>
              <p className="text-gray-400 mb-8">Perfect for getting started</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  10 videos/month
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  AI-powered search
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Smart clipping
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Basic analytics
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Email support
                </li>
              </ul>
              <Link
                href="/register"
                className="block w-full text-center border-2 border-blue-500 text-blue-400 px-6 py-3 rounded-lg font-semibold hover:bg-blue-500/10 transition"
              >
                Get Started
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl border-2 border-blue-500 relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$29</span>
                <span className="text-gray-400">/month</span>
              </div>
              <p className="text-gray-400 mb-8">For content creators and small teams</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Unlimited videos
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Advanced AI search
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Batch processing
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Advanced analytics
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Priority support
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  API access
                </li>
              </ul>
              <Link
                href="/register?plan=pro"
                className="block w-full text-center bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition shadow-lg shadow-blue-500/50"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl border border-gray-700">
              <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">Custom</span>
              </div>
              <p className="text-gray-400 mb-8">For large organizations</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Unlimited everything
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  White-label option
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Custom integrations
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  Dedicated support
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  SLA guarantee
                </li>
                <li className="flex items-center text-gray-300">
                  <span className="text-green-500 mr-2">✓</span>
                  On-premise option
                </li>
              </ul>
              <Link
                href="/contact?plan=enterprise"
                className="block w-full text-center border-2 border-gray-500 text-gray-300 px-6 py-3 rounded-lg font-semibold hover:border-blue-500 hover:text-white transition"
              >
                Contact Sales
              </Link>
            </div>

          </div>

          {/* FAQ Section */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently asked questions
            </h2>
            <div className="space-y-6">
              {[
                { q: 'Can I switch plans later?', a: 'Yes! You can upgrade, downgrade, or cancel your plan at any time.' },
                { q: 'What happens if I exceed my video limit?', a: 'You\'ll receive a notification and can upgrade your plan to continue processing videos.' },
                { q: 'Do you offer refunds?', a: 'Yes, we offer a 14-day money-back guarantee on all paid plans.' },
                { q: 'Is my data secure?', a: 'Yes, we use enterprise-grade encryption and SOC 2 compliance to protect your data.' }
              ].map((faq, idx) => (
                <details key={idx} className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700">
                  <summary className="text-white font-semibold cursor-pointer">{faq.q}</summary>
                  <p className="text-gray-400 mt-4">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

